import type { LucideIcon } from 'lucide-react';
import {
  PlusCircle,
  FileText,
  UploadCloud,
  Search,
  Palette,
  Settings,
  Download,
  Home,
  Edit2,
} from 'lucide-react';
import type { Tab } from '../../store/workspaceTabsStore';
import { APPEARANCE_MODES, type AppearanceMode } from '../../lib/appearanceModes';

export type TemplateContextMenuTarget = {
  templateId: string;
  isSystem: boolean;
  caseId?: string;
  draftsSubTab?: 'recommended' | 'caseDrafts';
};

export interface AppContextMenuState {
  open: boolean;
  x: number;
  y: number;
  templateContext?: TemplateContextMenuTarget;
}

export function getTemplateContextFromTarget(
  target: EventTarget | null,
): TemplateContextMenuTarget | null {
  if (!(target instanceof Element)) return null;
  const card = target.closest('[data-lexgat-template-card]');
  if (!card) return null;
  const templateId = card.getAttribute('data-lexgat-template-id');
  if (!templateId) return null;
  const draftsSubTab = card.getAttribute('data-lexgat-drafts-sub-tab');
  return {
    templateId,
    isSystem: card.getAttribute('data-lexgat-template-system') === 'true',
    caseId: card.getAttribute('data-lexgat-case-id') || undefined,
    draftsSubTab:
      draftsSubTab === 'recommended' || draftsSubTab === 'caseDrafts'
        ? draftsSubTab
        : undefined,
  };
}

export type AppContextMenuItem =
  | { type: 'divider'; id: string }
  | {
      type: 'action';
      id: string;
      label: string;
      icon?: LucideIcon;
      shortcut?: string;
      onClick: () => void;
    }
  | {
      type: 'submenu';
      id: string;
      label: string;
      icon?: LucideIcon;
      items: AppContextMenuSubItem[];
    };

export interface AppContextMenuSubItem {
  id: string;
  label: string;
  checked?: boolean;
  onClick: () => void;
}

export interface BuildAppContextMenuParams {
  activeTab: Tab | null;
  isTabbableRoute: boolean;
  appearance: AppearanceMode;
  isMac: boolean;
  isAdmin?: boolean;
  templateContext?: TemplateContextMenuTarget | null;
  onNewCase: () => void;
  onNewDraft: () => void;
  onNewDraftForCase: (caseId: string) => void;
  onEditTemplate?: (target: TemplateContextMenuTarget) => void;
  onImportCase: () => void;
  onSearch: () => void;
  onExportDocx: () => void;
  onNavigate: (path: string) => void;
  onSetAppearance: (mode: AppearanceMode) => void;
  onSettings: () => void;
}

export function buildAppContextMenu(params: BuildAppContextMenuParams): AppContextMenuItem[] {
  const {
    activeTab,
    isTabbableRoute,
    appearance,
    isMac,
    isAdmin = false,
    templateContext,
    onNewCase,
    onNewDraft,
    onNewDraftForCase,
    onEditTemplate,
    onImportCase,
    onSearch,
    onExportDocx,
    onNavigate,
    onSetAppearance,
    onSettings,
  } = params;

  const searchShortcut = isMac ? '⌘K' : 'Ctrl+K';
  const items: AppContextMenuItem[] = [];

  if (templateContext && onEditTemplate) {
    items.push({
      type: 'action',
      id: 'edit-template',
      label: isAdmin ? 'Edit this template' : 'Open template',
      icon: Edit2,
      onClick: () => onEditTemplate(templateContext),
    });
    items.push({ type: 'divider', id: 'ctx-divider-template' });
  }

  const showContextItems =
    isTabbableRoute && activeTab && (activeTab.type === 'case' || activeTab.type === 'draft');

  if (showContextItems && activeTab.type === 'case' && activeTab.entityId) {
    items.push({
      type: 'action',
      id: 'new-draft-for-case',
      label: 'New draft for this case',
      icon: FileText,
      onClick: () => onNewDraftForCase(activeTab.entityId!),
    });
    items.push({ type: 'divider', id: 'ctx-divider' });
  }

  if (
    showContextItems &&
    activeTab.type === 'draft' &&
    !activeTab.path.includes('/system/')
  ) {
    items.push({
      type: 'action',
      id: 'export-docx',
      label: 'Export DOCX',
      icon: Download,
      onClick: onExportDocx,
    });
    items.push({ type: 'divider', id: 'ctx-divider-draft' });
  }

  items.push(
    {
      type: 'action',
      id: 'new-case',
      label: 'New Case',
      icon: PlusCircle,
      onClick: onNewCase,
    },
    {
      type: 'action',
      id: 'new-draft',
      label: 'New Draft',
      icon: FileText,
      onClick: onNewDraft,
    },
    {
      type: 'action',
      id: 'import-case',
      label: 'Import Existing Practice',
      icon: UploadCloud,
      onClick: onImportCase,
    },
    {
      type: 'action',
      id: 'search',
      label: 'Search',
      icon: Search,
      shortcut: searchShortcut,
      onClick: onSearch,
    },
    { type: 'divider', id: 'div-appearance' },
    {
      type: 'submenu',
      id: 'appearance',
      label: 'Appearance',
      icon: Palette,
      items: APPEARANCE_MODES.map((mode) => ({
        id: mode.id,
        label: mode.label,
        checked: appearance === mode.id,
        onClick: () => onSetAppearance(mode.id),
      })),
    },
    { type: 'divider', id: 'motion-nav' },
    {
      type: 'submenu',
      id: 'go-to',
      label: 'Go to',
      icon: Home,
      items: [
        { id: 'nav-dashboard', label: 'Home', onClick: () => onNavigate('/dashboard') },
        { id: 'nav-cases', label: 'Cases', onClick: () => onNavigate('/cases') },
        { id: 'nav-drafts', label: 'Drafts', onClick: () => onNavigate('/drafts') },
        { id: 'nav-uploads', label: 'Uploads', onClick: () => onNavigate('/uploads') },
        { id: 'nav-diary', label: 'Diary', onClick: () => onNavigate('/diary') },
        { id: 'nav-library', label: 'Library', onClick: () => onNavigate('/library') },
      ],
    },
    {
      type: 'action',
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      onClick: onSettings,
    },
  );

  return items;
}

export function shouldShowGlobalContextMenu(event: MouseEvent): boolean {
  if (event.defaultPrevented) return false;

  const target = event.target;
  if (!(target instanceof Element)) return false;

  if (target.closest('.ProseMirror')) return false;
  if (target.closest('#editor-scroll-container')) return false;
  if (target.closest('[data-lexgat-local-context-menu]')) return false;
  if (target.closest('[data-lexgat-team-member-card]')) return false;
  if (target.closest('input, textarea, select')) return false;

  const contentEditable = target.closest('[contenteditable="true"]');
  if (contentEditable && !contentEditable.closest('.ProseMirror')) return false;

  return true;
}

export function clampMenuPosition(
  x: number,
  y: number,
  menuWidth: number,
  menuHeight: number,
): { x: number; y: number } {
  const padding = 8;
  let clampedX = x;
  let clampedY = y;

  if (clampedX + menuWidth > window.innerWidth - padding) {
    clampedX = Math.max(padding, window.innerWidth - menuWidth - padding);
  }
  if (clampedY + menuHeight > window.innerHeight - padding) {
    clampedY = Math.max(padding, window.innerHeight - menuHeight - padding);
  }

  return { x: clampedX, y: clampedY };
}
